import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Conversation, Message } from "@lumen/shared";
import { api } from "../../api";
import { Markdown } from "../../components/Markdown";
import {
  MessageActivity,
  MessageAttachments,
} from "../../components/chat/MessageActivity";

const API_BASE = (import.meta.env.VITE_API_BASE || "/api").replace(/\/$/, "");

export function AutomationRunPage() {
  const { id } = useParams();
  const [conversation, setConversation] = useState<Conversation>();
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    let streamedMessageId = "";
    const follow = async (conversationId: string, messageId: string) => {
      const response = await fetch(
        `${API_BASE}/conversations/${conversationId}/messages/stream?messageId=${encodeURIComponent(messageId)}`,
        { credentials: "include", headers: { accept: "text/event-stream" } },
      );
      if (!response.ok || !response.body) throw new Error("Stream unavailable");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const consume = (block: string) => {
        const data = block
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (!data) return;
        const event = JSON.parse(data) as {
          type: "sync" | "done" | "error";
          message?: Message;
          conversation?: Conversation;
        };
        if (!active) return;
        if (event.type === "sync" && event.message)
          setConversation((current) =>
            current
              ? {
                  ...current,
                  messages: (current.messages ?? []).map((message) =>
                    message.id === event.message!.id ? event.message! : message,
                  ),
                }
              : current,
          );
        if (
          (event.type === "done" || event.type === "error") &&
          event.conversation
        )
          setConversation(event.conversation);
      };
      while (active) {
        const { done, value } = await reader.read();
        buffer += decoder
          .decode(value, { stream: !done })
          .replace(/\r\n/g, "\n");
        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
          consume(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");
        }
        if (done) break;
      }
    };
    const load = () =>
      void api<Conversation>(`/automation-runs/${id}/conversation`)
        .then((value) => {
          if (active) {
            setConversation(value);
            setError("");
            const running = value.messages?.find(
              (message) =>
                message.role === "assistant" &&
                message.generationStatus === "running",
            );
            if (running && streamedMessageId !== running.id) {
              streamedMessageId = running.id;
              void follow(value.id, running.id).catch(() => {
                // The regular refresh below remains a safe fallback.
              });
            }
          }
        })
        .catch(() => {
          if (active) setError("This run is queued or still starting…");
        });
    load();
    // This also covers the queued phase, before the worker has created the
    // conversation and the message stream can be attached.
    const timer = window.setInterval(load, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [id]);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <header className="sticky top-0 z-10 border-b border-stone-200 bg-white/90 px-5 py-3 backdrop-blur dark:border-white/10 dark:bg-[#121615]/90">
        <Link to="/settings/automations" className="text-sm text-lumen-600">
          {t("← Automations")}
        </Link>
        <h1 className="mt-1 font-bold">
          {conversation?.title ?? t("Automation run")}
        </h1>
      </header>
      <div className="mx-auto max-w-3xl px-4 py-6">
        {error && !conversation && <p className="text-stone-500">{error}</p>}
        {conversation?.messages?.map((message) => (
          <article
            key={message.id}
            className="chat-message group mx-auto flex w-full max-w-3xl gap-4 px-4 py-6"
          >
            <div
              className={`grid size-8 shrink-0 place-items-center rounded-xl ${message.role === "assistant" ? "bg-lumen-600 text-white" : "bg-stone-200 text-stone-600 dark:bg-white/10 dark:text-stone-200"}`}
            >
              {message.role === "assistant" ? "✦" : "↗"}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="mb-1 text-xs font-bold tracking-wider text-stone-400 uppercase">
                {message.role === "assistant"
                  ? message.modelName || t("Assistant")
                  : t("Automation prompt")}
              </div>
              {message.role === "assistant" && (
                <MessageActivity
                  message={message}
                  busy={message.generationStatus === "running"}
                />
              )}
              {message.role !== "assistant" ||
              !message.activityEvents?.length ? (
                <Markdown streaming={message.generationStatus === "running"}>
                  {message.content}
                </Markdown>
              ) : null}
              <MessageAttachments message={message} />
              {message.error && (
                <p className="mt-2 text-sm text-red-600">{message.error}</p>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
